<script setup>
import { ref, computed } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import MessageStatus from '../MessageStatus.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { useMessageContext } from '../provider.js';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  isGuardedMediaUrl,
  useGuardedMedia,
} from 'dashboard/composables/useGuardedMedia';
import {
  ATTACHMENT_TYPES,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  ORIENTATION,
} from '../constants';

// Aceleres: bolha única que renderiza um álbum de imagens em grade.
// A detecção e o agrupamento acontecem em MessageList.vue; aqui consumimos
// `albumMessages` e mantemos os metadados de cada mensagem na miniatura.

const {
  albumMessages,
  orientation,
  messageType,
  filteredCurrentChatAttachments,
} = useMessageContext();
const PREVIEW_TILE_COUNT = 3;
const mediaLabels = {
  unavailable: 'Não foi possível carregar esta mídia.',
  retry: 'Tentar novamente',
};

const albumItems = computed(() => {
  const list = albumMessages?.value || [];
  return list
    .map(message => ({
      message,
      attachment: (message.attachments && message.attachments[0]) || null,
    }))
    .filter(item => item.attachment);
});

// O álbum abre dentro do histórico cronológico da própria conversa. O provider
// fixa essa origem pelo conversationId da mensagem.
const albumGalleryAttachments = computed(
  () => filteredCurrentChatAttachments.value
);

const total = computed(() => albumItems.value.length);

// Aceleres: video no album usa o mesmo tile quadrado/coberto da imagem,
// mas a fonte precisa ser o poster (thumbUrl) - dataUrl de video e o
// arquivo de midia em si, nao da pra usar em <img>.
const isVideoAttachment = attachment => {
  const attachmentType = String(
    attachment.fileType ||
      attachment.file_type ||
      attachment.contentType ||
      attachment.content_type ||
      ''
  ).toLowerCase();
  return (
    attachmentType === ATTACHMENT_TYPES.VIDEO ||
    attachmentType.startsWith('video/')
  );
};

const tileSrc = attachment => {
  const raw = isVideoAttachment(attachment)
    ? attachment.thumbUrl
    : attachment.dataUrl || attachment.thumbUrl;
  if (!raw) return '';
  return isGuardedMediaUrl(raw) ? guardedMediaSrc(raw) : raw;
};

const albumMobileGalleryAttachments = computed(() =>
  albumItems.value.map(({ attachment, message }) =>
    useSnakeCase({
      ...attachment,
      createdAt: attachment.createdAt || message.createdAt,
      messageId: attachment.messageId || message.id,
      sender: attachment.sender || message.sender,
    })
  )
);

const previewTiles = computed(() =>
  albumItems.value.slice(0, PREVIEW_TILE_COUNT + 1)
);
const remainingCount = computed(() =>
  Math.max(0, total.value - (PREVIEW_TILE_COUNT + 1))
);
const showSummaryOverlay = computed(() => remainingCount.value > 0);
const remainingLabel = computed(() => `+${remainingCount.value}`);

const albumBubbleBackgroundClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT ? '!bg-[#d9fdd3]' : '!bg-white'
);

const albumTileBackgroundClass = 'bg-white';

const readableTime = message => messageTimestamp(message?.createdAt, 'HH:mm');
const showSingleTimestamp = computed(
  () => messageType.value === MESSAGE_TYPES.INCOMING
);
const albumTimestampMessage = computed(
  () => albumItems.value[albumItems.value.length - 1]?.message
);

const statusToShow = message => {
  if (
    message?.messageType !== MESSAGE_TYPES.OUTGOING ||
    message?.private ||
    message?.status === MESSAGE_STATUS.FAILED
  ) {
    return null;
  }

  if (
    [
      MESSAGE_STATUS.SENT,
      MESSAGE_STATUS.DELIVERED,
      MESSAGE_STATUS.READ,
    ].includes(message.status)
  ) {
    return message.status;
  }

  return MESSAGE_STATUS.PROGRESS;
};

// O mosaico mostra quatro fotos: as três primeiras sem sobreposição e a quarta
// como a miniatura de resumo quando ainda existem fotos depois dela.
const gridColsClass = 'grid-cols-2';
const tileClass = index => [
  total.value === 3 && index === 0 ? 'col-span-2' : 'aspect-square',
  index === 0 ? '!rounded-tl-none' : '',
];
const tileStyle = index =>
  total.value === 3 && index === 0 ? { aspectRatio: '2 / 1' } : undefined;

const showGallery = ref(false);
const clickedAttachment = ref(null);
const unavailableAttachmentKeys = ref(new Set());
const retryAttempts = ref({});
const retriedMediaSources = ref({});
const { retryGuardedMedia, guardedMediaSrc } = useGuardedMedia();

const attachmentKey = attachment =>
  String(attachment?.id || attachment?.dataUrl || 'media');
const isUnavailable = attachment =>
  unavailableAttachmentKeys.value.has(attachmentKey(attachment));
const retryAttempt = attachment =>
  retryAttempts.value[attachmentKey(attachment)] || 0;
const failedMediaSrc = attachment =>
  isVideoAttachment(attachment) ? attachment.dataUrl : tileSrc(attachment);
const renderedMediaSrc = attachment =>
  retriedMediaSources.value[attachmentKey(attachment)] ||
  failedMediaSrc(attachment);

const markUnavailable = attachment => {
  unavailableAttachmentKeys.value = new Set([
    ...unavailableAttachmentKeys.value,
    attachmentKey(attachment),
  ]);
};

const retryMedia = async attachment => {
  const key = attachmentKey(attachment);
  const nextUnavailable = new Set(unavailableAttachmentKeys.value);
  nextUnavailable.delete(key);
  unavailableAttachmentKeys.value = nextUnavailable;
  retryAttempts.value = {
    ...retryAttempts.value,
    [key]: retryAttempt(attachment) + 1,
  };

  const source = failedMediaSrc(attachment);
  if (!isGuardedMediaUrl(source)) return;

  const resolved = await retryGuardedMedia(source);
  if (!resolved) {
    markUnavailable(attachment);
    return;
  }

  retriedMediaSources.value = {
    ...retriedMediaSources.value,
    [key]: resolved,
  };
};

const openGalleryAt = item => {
  clickedAttachment.value = item.attachment;
  showGallery.value = true;
};

const galleryAttachment = computed(() =>
  clickedAttachment.value ? useSnakeCase(clickedAttachment.value) : null
);
</script>

<template>
  <BaseBubble
    class="acl-image-album !w-[420px] !max-w-[calc(100vw-48px)] !rounded-tl-none overflow-visible pt-0 px-[5px] pb-[5px]"
    :class="albumBubbleBackgroundClass"
    data-bubble-name="image-album"
    hide-meta
    show-sender-name
  >
    <div
      class="acl-image-album__grid relative grid gap-1 overflow-hidden rounded-tr-lg rounded-br-lg rounded-bl-lg rounded-tl-none"
      :class="gridColsClass"
    >
      <div
        v-for="(item, index) in previewTiles"
        :key="item.attachment.id"
        class="relative cursor-pointer overflow-hidden rounded-md group"
        :class="[tileClass(index), albumTileBackgroundClass]"
        :style="tileStyle(index)"
        @click.stop="openGalleryAt(item)"
      >
        <div
          v-if="isUnavailable(item.attachment)"
          class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-n-alpha-2 px-3 text-center"
        >
          <Icon icon="i-lucide-circle-off" class="size-5 text-n-slate-11" />
          <span class="text-xs leading-4 text-n-slate-11">
            {{ mediaLabels.unavailable }}
          </span>
          <button
            type="button"
            class="text-xs font-medium text-n-brand underline"
            @click.stop="retryMedia(item.attachment)"
          >
            {{ mediaLabels.retry }}
          </button>
        </div>
        <video
          v-if="isVideoAttachment(item.attachment)"
          :key="`${attachmentKey(item.attachment)}-${retryAttempt(item.attachment)}`"
          class="w-full h-full object-cover skip-context-menu !bg-white"
          :src="renderedMediaSrc(item.attachment)"
          :poster="tileSrc(item.attachment) || undefined"
          preload="metadata"
          muted
          playsinline
          tabindex="-1"
          @error="markUnavailable(item.attachment)"
        />
        <img
          v-else
          :key="`${attachmentKey(item.attachment)}-${retryAttempt(item.attachment)}`"
          class="w-full h-full object-cover !bg-white"
          :src="renderedMediaSrc(item.attachment)"
          loading="lazy"
          @error="markUnavailable(item.attachment)"
        />
        <div
          class="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 bg-gradient-to-t from-[rgba(0,0,0,0.7)] via-[rgba(0,0,0,0.3)] to-transparent"
        />
        <div
          v-if="isVideoAttachment(item.attachment)"
          class="acl-image-album__play-badge pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
        >
          <span
            class="flex items-center justify-center rounded-full bg-[rgba(0,0,0,0.45)] text-white size-9"
          >
            <Icon icon="i-lucide-play" class="size-4" />
          </span>
        </div>
        <div
          v-if="index === PREVIEW_TILE_COUNT && showSummaryOverlay"
          class="acl-image-album__summary-overlay pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-[rgba(0,0,0,0.7)] text-[2rem] font-normal leading-none text-white"
        >
          {{ remainingLabel }}
        </div>
        <div
          v-if="!showSingleTimestamp"
          class="pointer-events-none absolute bottom-1 right-1 z-30 flex items-center gap-0.5 text-[13px] leading-none text-white drop-shadow-md"
        >
          <time>{{ readableTime(item.message) }}</time>
          <MessageStatus
            v-if="statusToShow(item.message)"
            :status="statusToShow(item.message)"
          />
        </div>
      </div>
      <div
        v-if="showSingleTimestamp && albumTimestampMessage"
        class="pointer-events-none absolute bottom-1 right-1 z-30 flex items-center text-[13px] leading-none text-white drop-shadow-md"
      >
        <time>{{ readableTime(albumTimestampMessage) }}</time>
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery && galleryAttachment"
    v-model:show="showGallery"
    :attachment="galleryAttachment"
    :all-attachments="albumGalleryAttachments"
    :mobile-overview-attachments="albumMobileGalleryAttachments"
    @close="() => (showGallery = false)"
  />
</template>
